import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./AllRewardsPage.css";
import styled from "styled-components";
import axios from "axios";
import coinIcon from "../assets/coin.png";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import openNotification from "components/utils/CustomNotification";
import { MdEdit, MdDelete } from "react-icons/md";

const beAddress = process.env.REACT_APP_API_URL;

interface RewardItem {
  id: string;
  reward_uid: string;
  reward_type: "voucher" | "coupon";
  title: string;
  sub_title: string;
  expiry_date: string;
  active_status: boolean;
  expired?: boolean;
  redeemable?: boolean;
  amount: number;
  image_details: {
    image_uid: string;
    image_url?: string;
  };
  reward_details: {
    terms_and_conditions: string;
    steps_to_redeem: string[];
    usage: string;
    discount?: string;
    discount_type?: string;
    dis_value?: number;
  };
}
const initialRewardState = {
  org_uid: "eminds",
  reward_type: "",
  reward_name: "",
  image: "",
  description: "",
  platform: "",
  amount: 0,
  currency: "",
  discount_type: "",
  dis_value: 0,
  code: "",
  expiry_datetime: "",
  redeem_guide: "",
};

const AllRewardsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessionUid, setSessionUid] = useState("");
  const [userUid, setUserUid] = useState("");
  const [rewards, setRewards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState<any>(null);
  const [showCode, setShowCode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingReward, setDeletingReward] = useState<any>(null);
  const privilege = localStorage.getItem("privilege") ?? "";
  const [searchTerm, setSearchTerm] = useState("");

  const params = new URLSearchParams(location.search);
  const type = params.get("type");
  const tab = params.get("tab");
  const isEarnedSection = type === "earned";
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReward, setEditingReward] = useState<any>(null);
  const [isedit, setIsedit] = useState(false);
  const [newReward, setNewReward] = useState(initialRewardState);

  useEffect(() => {
    const s = localStorage.getItem("session_uid") || "";
    const u = localStorage.getItem("user_uid") || "";
    setSessionUid(s);
    setUserUid(u);
  }, []);

  useEffect(() => {
    if (!sessionUid || !userUid) return;
    fetchAllRewards();
  }, [sessionUid, userUid, type, tab]);

  const handleRedeemReward = async (rewardUid: string) => {
    try {
      const response = await axios.get(`${beAddress}/api/redeem-reward`, {
        params: {
          reward_uid: rewardUid,
        },
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      const { status, message } = response.data;

      if (status === "Success") {
        if (typeof openNotification !== "undefined") {
          openNotification({
            type: "success",
            message: message || "Reward redeemed successfully!",
          });
        } else {
          alert("Reward redeemed successfully!");
        }

        // Refresh the rewards
        await fetchAllRewards();

        // Close the modal after successful redemption
        handleCloseModal();
      } else {
        if (typeof openNotification !== "undefined") {
          openNotification({
            type: "error",
            message: message || "Failed to redeem reward.",
          });
        } else {
          alert("Failed to redeem reward: " + message);
        }
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to redeem reward. Please try again.";

      if (typeof openNotification !== "undefined") {
        openNotification({
          type: "error",
          message: errorMsg,
        });
      } else {
        alert("Error: " + errorMsg);
      }
    }
  };

  // Function to handle buying a reward
  const handleBuyReward = async (reward: any) => {
    if (type !== "available") return;

    if (!reward.redeemable) {
      if (typeof openNotification !== "undefined") {
        openNotification({
          type: "warning",
          message: "This reward is not redeemable at the moment.",
        });
      } else {
        alert("This reward is not redeemable at the moment.");
      }
      return;
    }

    try {
      // Use the redeem-reward API directly without confirmation dialog
      await handleRedeemReward(reward.reward_uid);
    } catch (error: any) {
      console.error("Error purchasing reward:", error);
      const errorMsg = error?.message || "Failed to purchase reward.";

      if (typeof openNotification !== "undefined") {
        openNotification({
          type: "error",
          message: errorMsg,
        });
      } else {
        alert("Error: " + errorMsg);
      }
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRewardChange = (e: any) => {
    const { name, value, type } = e.target;
    const noEmojiValue = value.replace(
      /[\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{2600}-\u{26FF}]/gu,
      "",
    );
    if (type === "number" && Number(noEmojiValue) < 0) return;

    // When discount_type changes to fixed, mirror amount into dis_value
    if (name === "discount_type") {
      setNewReward((prev) => ({
        ...prev,
        [name]: noEmojiValue,
        dis_value: noEmojiValue === "fixed" ? prev.amount : prev.dis_value,
      }));
      return;
    }

    setNewReward((prev) => {
      const updated = {
        ...prev,
        [name]: type === "number" ? Number(noEmojiValue) : noEmojiValue,
      };
      // Keep dis_value in sync with amount when discount_type is fixed
      if (name === "amount" && prev.discount_type === "fixed") {
        updated.dis_value = Number(noEmojiValue);
      }
      return updated;
    });
  };

  const handleEditReward = (reward: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingReward(reward);
    setIsedit(true);

    // Extract redeem_guide from the correct location
    // Check multiple possible locations for redeem guide
    const redeemGuide =
      reward.redeem_guide ||
      (Array.isArray(reward.reward_details?.steps_to_redeem)
        ? reward.reward_details.steps_to_redeem.join("\n")
        : reward.reward_details?.steps_to_redeem || "");

    setNewReward({
      org_uid: reward.org_uid || "eminds",
      reward_type: reward.reward_type,
      reward_name: reward.reward_name || reward.title,
      image: reward.image_details?.image_url || "",
      description: reward.description || "",
      platform: reward.platform || "",
      amount: reward.amount || 0,
      currency: reward.currency || "INR",
      discount_type: reward.discount_type || "",
      dis_value:
        reward.discount_type === "fixed" // ✅ top-level
          ? Number(reward.amount)
          : Number(reward.dis_value) || 0, // ✅ top-level
      code: reward.code || "",
      expiry_datetime: reward.expiry_datetime || "",
      redeem_guide: redeemGuide,
    });
    setShowCreateModal(true);

    const dropdown = e.currentTarget.parentElement;
    if (dropdown) dropdown.classList.remove("show");
  };

  const handleSubmitReward = async () => {
    try {
      const changedValues: any = {};

      // Always include org_uid
      changedValues.org_uid = editingReward.org_uid || "eminds";

      if (
        newReward.reward_name !==
        (editingReward.reward_name || editingReward.title)
      )
        changedValues.reward_name = newReward.reward_name;

      if (newReward.description !== (editingReward.description || ""))
        changedValues.description = newReward.description;

      if (newReward.platform !== (editingReward.platform || ""))
        changedValues.platform = newReward.platform;

      if (Number(newReward.amount) !== Number(editingReward.amount || 0))
        changedValues.amount = newReward.amount;

      if (newReward.currency !== (editingReward.currency || "INR"))
        changedValues.currency = newReward.currency;

      if (
        newReward.code !==
        (editingReward.reward_details?.usage || editingReward.code || "")
      )
        changedValues.code = newReward.code;

      if (newReward.expiry_datetime !== (editingReward.expiry_datetime || ""))
        changedValues.expiry_datetime = newReward.expiry_datetime;

      if (newReward.redeem_guide !== (editingReward.redeem_guide || ""))
        changedValues.redeem_guide = newReward.redeem_guide;

      if (
        newReward.image &&
        newReward.image !== (editingReward.image_details?.image_url || "")
      )
        changedValues.image = newReward.image;

      // Only include discount fields if reward is a coupon and they changed
      if (newReward.reward_type === "coupon") {
        const origDiscountType = editingReward.discount_type || "";
        const origDisValue = Number(editingReward.dis_value || 0);

        const newDisValue =
          newReward.discount_type === "fixed"
            ? Number(newReward.amount)
            : Number(newReward.dis_value);

        if (newReward.discount_type !== origDiscountType)
          changedValues.discount_type = newReward.discount_type;

        if (newDisValue !== origDisValue) changedValues.dis_value = newDisValue;
      }

      // Only org_uid means no real changes
      if (Object.keys(changedValues).length === 1) {
        openNotification({ type: "info", message: "No changes detected" });
        return;
      }

      const payload = [
        { reward_uid: editingReward.reward_uid, ...changedValues },
      ];

      const response = await axios.put(`${beAddress}/api/rewards`, payload, {
        headers: { "user-uid": userUid, "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (response.data.status === "Success") {
        openNotification({
          type: "success",
          message: "Reward updated successfully!",
        });
        setShowCreateModal(false);
        setEditingReward(null);
        setNewReward(initialRewardState);
        fetchAllRewards();
      } else {
        openNotification({
          type: "error",
          message:
            response.data?.data || response.data.message || "Update failed.",
        });
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.data ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update reward.";
      openNotification({ type: "error", message: errorMsg });
    }
  };

  const fetchImagesByUid = async (
    imageUids: string[],
  ): Promise<Record<string, string>> => {
    const limitedUids = imageUids.slice(0, 50);
    if (limitedUids.length === 0) return {};

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      limitedUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid),
      );
      url.searchParams.set("is_icon_img", "true");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: { "user-uid": userUid },
        credentials: "include",
      });
      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => [
            item.image_uid,
            item.default ? "" : item.image,
          ]),
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }
    return {};
  };

  const fetchAllRewards = async () => {
    try {
      setLoading(true);

      const filterType = tab === "coupons" ? "coupon" : "voucher";
      if (type === "available") {
        // Prepare payload for fetching available rewards
        const payload = {
          reward_status: "unused",
          expired: false,
        };

        const response = await axios.get(`${beAddress}/api/rewards`, {
          params: payload, // Pass payload as query parameters
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        });

        const allRewards: RewardItem[] = response.data?.data || [];
        const imageUids = allRewards.map((r) => r.image_details.image_uid);
        const imageMap = await fetchImagesByUid(imageUids);

        const rewardsWithImages = allRewards.map((r) => ({
          ...r,
          image_details: {
            ...r.image_details,
            image_url: imageMap[r.image_details.image_uid] || "",
          },
        }));

        setRewards(
          rewardsWithImages.filter((r: any) => r.reward_type === filterType),
        );
      } else {
        const response = await axios.get(`${beAddress}/api/user-rewards`, {
          headers: { "user-uid": userUid },
          withCredentials: true,
        });

        const userRewardsList = response.data?.data?.rewards || [];

        if (userRewardsList.length > 0) {
          const imageUids = userRewardsList
            .map((r: any) => r.image_details?.image_uid)
            .filter(Boolean);
          const imageMap = await fetchImagesByUid(imageUids);

          const rewardsWithImages = userRewardsList.map((r: any) => ({
            ...r,
            title: r.reward_name,
            redeemable: true,
            image_details: {
              ...r.image_details,
              image_url: imageMap[r.image_details?.image_uid] || "",
            },
            code: r.metadata?.code || "",
            reward_details: {
              ...r.reward_details,
              usage: r.metadata?.code,
            },
          }));

          // Use filterType instead of tab
          setRewards(
            rewardsWithImages.filter((r: any) => r.reward_type === filterType),
          );
        } else {
          setRewards([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch rewards:", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleDeleteReward = async () => {
  //   try {
  //     // Create the request body as an array containing the reward_uid
  //     const requestBody = [deletingReward.reward_uid];

  //     const response = await axios.delete(`${beAddress}/api/rewards`, {
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //         "Content-Type": "application/json",
  //       },
  //       data: requestBody, // Send as array
  //     });

  //     const { status } = response.data;

  //     if (status === "Success") {
  //       alert("Reward deleted successfully!");
  //       fetchAllRewards();
  //       setShowDeleteModal(false);
  //       setDeletingReward(null);
  //     } else {
  //       alert("Delete failed: " + response.data.message);
  //     }
  //   } catch (error: any) {
  //     console.error("Delete error:", error);
  //     alert("Failed to delete reward. Please try again.");
  //   }
  // };

  const handleDeleteReward = async () => {
    try {
      // Create the request body as an array containing the reward_uid
      const requestBody = [deletingReward.reward_uid];

      const response = await axios.delete(`${beAddress}/api/rewards`, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: requestBody, // Send as array
      });

      const { status, message } = response.data;

      if (status === "Success") {
        // Use the notification system if available, otherwise use alert
        if (typeof openNotification !== "undefined") {
          openNotification({
            type: "success",
            message: message || "Reward deleted successfully!",
          });
        } else {
          alert("Reward deleted successfully!");
        }

        fetchAllRewards();
        setShowDeleteModal(false);
        setDeletingReward(null);
      } else {
        // Use the notification system if available, otherwise use alert
        if (typeof openNotification !== "undefined") {
          openNotification({
            type: "error",
            message: message || "Delete reward failed.",
          });
        } else {
          alert("Delete failed: " + (message || "Unknown error"));
        }
      }
    } catch (error: any) {
      console.error("Delete error:", error);

      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete reward.";

      // Use the notification system if available, otherwise use alert
      if (typeof openNotification !== "undefined") {
        openNotification({
          type: "error",
          message: errorMsg,
        });
      } else {
        alert("Error: " + errorMsg);
      }
    }
  };
  const handleCloseModal = () => {
    setSelectedReward(null);
    setShowCode(false);
  };

  if (loading) return <div className="RewardsMessage">Loading...</div>;
  const filteredRewards = rewards.filter((r) =>
    (r.title || r.reward_name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="all-vouchers-page">
      <h2 className="page-title">
        {type === "earned" ? `All Earned ${tab}` : `All Available ${tab}`}
      </h2>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder={`Search ${tab}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rewards-search-input"
        />
      </div>

      {/* Grid Container */}
      <div className="all-deals-container">
        {filteredRewards.length > 0 ? (
          filteredRewards.map((deal, index) => {
            const isRedeemable = deal.redeemable !== false;

            return (
              <div
                key={deal.id || index}
                className={`all-deal-card ${
                  deal.expired ? "expired-card" : ""
                } ${
                  index % 3 === 0
                    ? "green-card"
                    : index % 3 === 1
                      ? "purple-card"
                      : "blue-card"
                } ${
                  !isRedeemable && type === "available"
                    ? "unredeemable-card"
                    : ""
                }`}
                style={{ width: "auto" }}
                onClick={() => setSelectedReward(deal)}
              >
                {(privilege === "systemadmin" || privilege === "orgadmin") &&
                  type === "available" && (
                    <>
                      <button
                        className="card-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.currentTarget.nextElementSibling?.classList.toggle(
                            "show",
                          );
                        }}
                      >
                        ⋮
                      </button>
                      <div className="card-dropdown">
                        <button onClick={(e) => handleEditReward(deal, e)}>
                          <MdEdit /> Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingReward(deal);
                            setShowDeleteModal(true);
                            e.currentTarget.parentElement?.classList.remove(
                              "show",
                            );
                          }}
                        >
                          <MdDelete /> Delete
                        </button>
                      </div>
                    </>
                  )}

                <img
                  src={
                    deal.image_details?.image_url
                      ? `data:image/png;base64,${deal.image_details.image_url}`
                      : coinIcon
                  }
                  alt={deal.title || "Reward"}
                  className="deal-card-image"
                  style={{
                    width: "100%",
                    height: "120px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    marginBottom: "10px",
                  }}
                />

                {/* Card text content */}
                <p>{deal.title || deal.reward_name}</p>

                {/* Discount Badge */}
                {deal.reward_type === "coupon" &&
                  deal.reward_details?.discount_type === "percentage" && (
                    <div className="discount-badge">
                      {deal.reward_details.dis_value}% OFF
                    </div>
                  )}

                <span>
                  {type === "earned" ? "Value: ₹" : "Price: ₹"}
                  {deal.amount}
                </span>

                {/* Redeemable status indicator */}

                {/* Button with redeemable logic */}
                <button
                  className="all-buy-btn"
                  disabled={deal.expired || !isRedeemable}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (type === "available" && isRedeemable) {
                      handleBuyReward(deal);
                    } else if (type === "available" && !isRedeemable) {
                      if (typeof openNotification !== "undefined") {
                        openNotification({
                          type: "warning",
                          message:
                            "This reward is not redeemable at the moment.",
                        });
                      } else {
                        alert("This reward is not redeemable at the moment.");
                      }
                    } else {
                      setSelectedReward(deal);
                    }
                  }}
                >
                  {deal.expired
                    ? "Expired"
                    : !isRedeemable && type === "available"
                      ? "Buy"
                      : type === "earned"
                        ? "Redeem"
                        : "Purchase"}
                </button>
              </div>
            );
          })
        ) : (
          <div className="no-results">No rewards found.</div>
        )}
      </div>

      {/* Modals Section (Keep existing code for Modals) */}
      {selectedReward && (
        <div className="voucher-modal-overlay" onClick={handleCloseModal}>
          <div
            className="voucher-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="voucher-close-btn" onClick={handleCloseModal}>
              ✕
            </button>

            <div className="voucher-header">
              <div className="voucher-brand-section">
                <div className="voucher-brand-logo">
                  <img
                    src={
                      selectedReward.image_details?.image_url
                        ? `data:image/png;base64,${selectedReward.image_details.image_url}`
                        : coinIcon
                    }
                    alt={selectedReward.title}
                  />
                </div>
                <div className="voucher-brand-info">
                  <h3 className="voucher-title">
                    {selectedReward.title || selectedReward.reward_name}
                  </h3>
                  <p className="voucher-reward-name">
                    {selectedReward.reward_name}
                  </p>
                  <h2 className="voucher-amount">₹{selectedReward.amount}</h2>
                </div>
              </div>
            </div>

            <div className="voucher-body">
              {selectedReward.expiry_datetime && (
                <div className="voucher-expiry">
                  <span className="voucher-expiry-icon">⏰</span>
                  <p className="voucher-expiry-text">
                    Valid till{" "}
                    {new Date(
                      selectedReward.expiry_datetime,
                    ).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              )}

              {/* Redeemable status in modal */}

              {selectedReward.description && (
                <div className="voucher-section">
                  <h4 className="section-title">
                    <span>📝</span> Offer Details
                  </h4>
                  <div className="section-content">
                    {selectedReward.description}
                  </div>
                </div>
              )}

              {selectedReward.redeem_guide && (
                <div className="voucher-section">
                  <h4 className="section-title">
                    <span>📋</span> How to Redeem
                  </h4>
                  <div className="section-content">
                    {selectedReward.redeem_guide}
                  </div>
                </div>
              )}

              {!showCode ? (
                <button
                  className={`voucher-btn voucher-btn-primary voucher-btn-full ${
                    type === "available" && selectedReward.redeemable === false
                      ? "disabled-btn"
                      : ""
                  }`}
                  onClick={() => {
                    if (type === "available") {
                      if (selectedReward.redeemable !== false) {
                        handleBuyReward(selectedReward);
                      } else {
                        if (typeof openNotification !== "undefined") {
                          openNotification({
                            type: "warning",
                            message:
                              "This reward is not redeemable at the moment.",
                          });
                        } else {
                          alert("This reward is not redeemable at the moment.");
                        }
                      }
                    } else {
                      setShowCode(true);
                    }
                  }}
                  disabled={
                    type === "available" && selectedReward.redeemable === false
                  }
                >
                  {selectedReward.expired ? (
                    <>
                      <span>🚫</span> Expired
                    </>
                  ) : type === "available" &&
                    selectedReward.redeemable === false ? (
                    <>
                      <span>🔒</span> Buy with{" "}
                      {selectedReward.points
                        ? parseFloat(Number(selectedReward.points).toFixed(2))
                        : 0}{" "}
                      Points
                    </>
                  ) : isEarnedSection ? (
                    <>
                      <span>🎉</span> Get Code
                    </>
                  ) : (
                    <>
                      <span>🛒</span> Buy with{" "}
                      {selectedReward.points
                        ? parseFloat(Number(selectedReward.points).toFixed(2))
                        : 0}{" "}
                      Points
                    </>
                  )}
                </button>
              ) : (
                <>
                  <div className="voucher-code-section">
                    <p className="code-label">Your Coupon Code</p>
                    <div className="code-display">
                      <div className="code-text">
                        {selectedReward.reward_details?.usage ||
                          selectedReward.code}
                      </div>
                      <button
                        className="copy-code-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            selectedReward.reward_details?.usage ||
                              selectedReward.code,
                          );
                          alert("Copied!");
                        }}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>
                  <div className="voucher-actions">
                    <button
                      className="voucher-btn voucher-btn-secondary"
                      onClick={() => setShowCode(false)}
                    >
                      ← Back
                    </button>
                    {/* <button
                      className="voucher-btn voucher-btn-primary"
                      onClick={() => {
                        // For earned rewards, mark as redeemed
                        if (type === "earned") {
                          alert("Redeemed!");
                          handleCloseModal();
                        }
                      }}
                    >
                      ✓ Redeem Now
                    </button> */}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <ModalOverlay onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <h3>Delete Reward?</h3>
              <p>Are you sure you want to remove "{deletingReward?.title}"?</p>
              <div className="delete-modal-actions">
                <button
                  className="delete-cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleDeleteReward}
                >
                  Delete
                </button>
              </div>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* --- EDIT REWARD MODAL --- */}
      {showCreateModal && (
        <ModalOverlay
          onClick={() => {
            setShowCreateModal(false);
            setEditingReward(null);
            setNewReward(initialRewardState);
          }}
        >
          <ModalContent
            className="wide-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Edit Reward</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingReward(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-form-grid">
              {/* Row 1 */}
              <div className="form-group">
                <label>Organization</label>
                <select
                  name="org_uid"
                  value={newReward.org_uid}
                  onChange={handleRewardChange}
                  disabled
                >
                  <option value="eminds">eminds</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reward Type</label>
                <select
                  name="reward_type"
                  value={newReward.reward_type}
                  onChange={handleRewardChange}
                  disabled
                >
                  <option value="">Select Type</option>
                  <option value="voucher">Voucher</option>
                  <option value="coupon">Coupon</option>
                </select>
              </div>

              {/* Row 2 */}
              <div className="form-group">
                <label>Platform Name</label>
                <input
                  type="text"
                  name="platform"
                  value={newReward.platform}
                  onChange={handleRewardChange}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Reward Title</label>
                <input
                  type="text"
                  name="reward_name"
                  value={newReward.reward_name}
                  onChange={handleRewardChange}
                />
              </div>

              {/* Description - Full Width */}
              <div className="form-group full-width">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  value={newReward.description}
                  onChange={handleRewardChange}
                />
              </div>

              {/* Row 3 */}
              <div className="form-group">
                <label>Amount / Points</label>
                <input
                  type="number"
                  name="amount"
                  value={newReward.amount}
                  onChange={handleRewardChange}
                />
              </div>
              {/* <div className="form-group">
                                <label>Currency</label>
                                <input type="text" name="currency" value={newReward.currency} onChange={handleRewardChange} />
                            </div> */}
              <div className="form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={newReward.currency}
                  onChange={handleRewardChange}
                >
                  <option value="INR">INR</option>
                </select>
              </div>

              {/* Coupon Specifics */}
              <div className="form-group">
                <label>Discount Type</label>
                <select
                  name="discount_type"
                  value={newReward.discount_type}
                  onChange={handleRewardChange}
                  disabled={newReward.reward_type !== "coupon"}
                >
                  <option value="">Select</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed</option>
                </select>
              </div>
              <div className="form-group">
                <label>Discount Value</label>
                <input
                  type="number"
                  name="dis_value"
                  placeholder="0"
                  value={
                    newReward.discount_type === "fixed"
                      ? newReward.amount || "" // always mirrors amount
                      : newReward.dis_value === 0
                        ? ""
                        : newReward.dis_value
                  }
                  onChange={handleRewardChange}
                  disabled={
                    newReward.reward_type !== "coupon" ||
                    newReward.discount_type === "fixed" ||
                    newReward.discount_type === ""
                  }
                  style={
                    newReward.discount_type === "fixed"
                      ? { backgroundColor: "#f0f0f0", cursor: "not-allowed" }
                      : {}
                  }
                  min="0"
                  max={newReward.discount_type === "percentage" ? "100" : ""}
                />
                {newReward.discount_type === "percentage" &&
                  Number(newReward.dis_value) > 100 && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#ff4d4f",
                        marginTop: "4px",
                      }}
                    >
                      Percentage cannot exceed 100%
                    </span>
                  )}
              </div>

              {/* Row 4 */}
              <div className="form-group">
                <label>Redeem Code</label>
                <input
                  type="text"
                  name="code"
                  value={newReward.code}
                  onChange={handleRewardChange}
                />
              </div>
              <div className="form-group">
                <label>Expiry Date</label>
                <Flatpickr
                  options={{
                    enableTime: true,
                    dateFormat: "Y-m-d H:i",
                    minDate: "today",
                    time_24hr: true,
                  }}
                  value={newReward.expiry_datetime}
                  onChange={(date) => {
                    // FIXED: Use functional update
                    setNewReward((prev) => ({
                      ...prev,
                      expiry_datetime: date[0] ? date[0].toISOString() : "",
                    }));
                  }}
                  className="date-input"
                />
              </div>

              {/* Instructions - Full Width */}
              <div className="form-group full-width">
                <label>Redeem Instructions</label>
                <textarea
                  name="redeem_guide"
                  rows={3}
                  value={newReward.redeem_guide || ""}
                  onChange={handleRewardChange}
                  placeholder="Enter step-by-step instructions..."
                />
              </div>

              {/* Image Upload - Full Width */}
              <div className="form-group full-width">
                <label>Reward Image</label>
                <div className="image-upload-wrapper">
                  {newReward.image && (
                    <div className="image-preview">
                      <img
                        src={
                          newReward.image.startsWith("data:")
                            ? newReward.image
                            : `data:image/png;base64,${newReward.image}`
                        }
                        alt="Preview"
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files?.[0] || null;
                      if (!file) return;
                      const base64 = await convertToBase64(file);
                      setNewReward((prev) => ({ ...prev, image: base64 })); // Use functional update
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingReward(null);
                }}
              >
                Cancel
              </button>
              <button className="submit-btn" onClick={handleSubmitReward}>
                Update Reward
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  width: 90%;
  max-width: 480px;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

export default AllRewardsPage;
